import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Save, GitCompare, Trash2, Copy, CheckCircle2, AlertTriangle, Loader2, Send, BookmarkPlus, Layers } from "lucide-react";
import { toast } from "sonner";

const HORIZONS = [7, 14, 30, 90];

/** Save / list / compare / apply named simulator scenarios, with a domain-scoped apply preview. */
export default function Scenarios({ athleteId, sim, recovery, taper, target }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [horizon, setHorizon] = useState(28);
  const [picked, setPicked] = useState([]);
  const [cmp, setCmp] = useState(null);
  const [applying, setApplying] = useState(null);
  const [preview, setPreview] = useState(null);
  const [ack, setAck] = useState([]);
  const [applyTo, setApplyTo] = useState("athlete");   // athlete | squad | picked
  const [squad, setSquad] = useState("");
  const [scale, setScale] = useState(true);
  const [skipUnavailable, setSkipUnavailable] = useState(true);
  const [roster, setRoster] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [pickedAthletes, setPickedAthletes] = useState([]);

  useEffect(() => { api.get("/athletes").then((r) => setRoster(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    if (athleteId) api.get(`/scenarios?athlete_id=${athleteId}`).then((r) => setList(r.data.scenarios || [])).catch(() => {});
    api.get("/scenario-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, [athleteId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!name.trim()) return toast.error("Give the scenario a name");
    if (!sim?.modified) return toast.error("Run a simulation with a change first");
    const curMap = Object.fromEntries((sim.current.trajectory || []).map((p) => [p.date, p]));
    const days = (sim.modified.trajectory || []).map((m) => {
      const c = curMap[m.date] || {};
      return {
        date: m.date, readiness: m.readiness, rest: m.rest, sharpness: m.sharpness, effort: m.effort,
        injury_risk: m.injury_risk, load: m.load, sessions: m.sessions ?? 0,
        changed: Math.abs((m.readiness ?? 0) - (c.readiness ?? 0)) >= 0.5 || Math.abs((m.load ?? 0) - (c.load ?? 0)) >= 0.05,
        new_day_type: recovery.includes(m.date) ? "recovery" : "",
      };
    });
    try {
      await api.post("/scenarios", {
        athlete_id: athleteId, name: name.trim(), horizon_days: Number(horizon),
        params: { recovery_dates: recovery, taper_days: Number(taper), target_date: target },
        projection: { days },
        drivers: [{ code: "recovery", days: recovery.length }, { code: "taper", days: Number(taper) }],
      });
      toast.success(`Scenario "${name.trim()}" saved`);
      setName(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the scenario"); }
  };

  const compare = async () => {
    if (picked.length < 2) return toast.error("Pick at least two scenarios to compare");
    const { data } = await api.post("/scenarios/compare", { scenario_ids: picked });
    setCmp(data.scenarios || []);
  };

  const applyBody = () => ({
    scale_to_baseline: scale,
    skip_unavailable: skipUnavailable,
    ...(applyTo === "squad" ? { squad } : {}),
    ...(applyTo === "picked" ? { athlete_ids: pickedAthletes } : {}),
  });

  const openApply = async (s) => {
    setApplying(s.scenario_id); setAck([]);
    if (s.is_template) setApplyTo("squad");
    try {
      const { data } = await api.post(`/scenarios/${s.scenario_id}/apply`, { preview: true, ...applyBody() });
      setPreview({ ...data, scenario: s });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build the preview"); }
    finally { setApplying(null); }
  };

  const refreshPreview = async () => {
    if (!preview) return;
    const { data } = await api.post(`/scenarios/${preview.scenario.scenario_id}/apply`, { preview: true, ...applyBody() });
    setPreview({ ...data, scenario: preview.scenario });
  };

  const confirmApply = async () => {
    const s = preview.scenario;
    setApplying(s.scenario_id);
    try {
      const { data } = await api.post(`/scenarios/${s.scenario_id}/apply`, { acknowledged: ack, ...applyBody() });
      toast.success(`Applied "${s.name}" to ${data.applied} athlete(s)`, {
        description: "Every athlete's previous calendar is saved · roll it back from their Change Log.",
      });
      setPreview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    finally { setApplying(null); }
  };

  const request = async (s, domain) => {
    try {
      await api.post("/change-requests", {
        athlete_id: s.athlete_id, domain,
        summary: `Apply scenario "${s.name}" (${domain} change)`,
        detail: { scenario_id: s.scenario_id },
      });
      toast.success("Request sent to the head coach");
      setPreview(null);
    } catch (e) { toast.error("Could not send the request"); }
  };

  const allWarnings = (preview?.athletes || []).flatMap((a) => a.warnings || []);
  const targets = (preview?.athletes || []).filter((a) => !a.excluded);
  const canApply = targets.length > 0 && targets.every((a) => a.can_apply);
  const squads = Array.from(new Set(roster.map((a) => a.squad))).filter(Boolean);
  const needAck = allWarnings.filter((w) => w.severity !== "low");
  const ackDone = needAck.every((w) => ack.includes(w.code)) || ["admin", "owner", "coach"].includes(user?.role);

  return (
    <div className="border border-border mt-6" data-testid="scenarios-panel">
      <div className="px-4 py-3 border-b border-border font-heading font-bold tracking-tight flex items-center gap-2">
        <Save className="w-4 h-4" /> Scenarios
        <span className="text-xs text-muted-foreground font-normal ml-auto">{list.length} saved</span>
      </div>

      <div className="p-4 flex flex-wrap gap-2 items-end border-b border-border">
        <label className="flex-1 min-w-[180px]">
          <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Scenario name</span>
          <input data-testid="scenario-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Deload into Ranji week 3" className="inp mt-1" />
        </label>
        <label>
          <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Horizon</span>
          <select data-testid="scenario-horizon" value={horizon} onChange={(e) => setHorizon(e.target.value)} className="inp mt-1 w-28">
            {HORIZONS.map((h) => <option key={h} value={h}>{`${h} days`}</option>)}
            <option value={28}>28 days</option>
          </select>
        </label>
        <button data-testid="scenario-save" onClick={save} className="text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors flex items-center gap-1.5">
          <Save className="w-3.5 h-3.5" /> Save scenario
        </button>
      </div>

      {list.length > 0 && (
        <div className="scroll-x">
          <table className="w-full text-sm" data-testid="scenario-table">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="p-2"></th><th className="text-left p-2">Scenario</th><th className="text-left p-2">Horizon</th>
                <th className="text-left p-2">Author</th><th className="text-left p-2">Created</th><th className="text-left p-2">Changed days</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => {
                const days = (s.projection?.days || []);
                return (
                  <tr key={s.scenario_id} className="border-b border-border last:border-0" data-testid={`scenario-row-${s.scenario_id}`}>
                    <td className="p-2">
                      <input type="checkbox" data-testid={`scenario-pick-${s.scenario_id}`} checked={picked.includes(s.scenario_id)}
                        onChange={(e) => setPicked((p) => e.target.checked ? [...p, s.scenario_id] : p.filter((x) => x !== s.scenario_id))} />
                    </td>
                    <td className="p-2 font-medium">{s.name}</td>
                    <td className="p-2 tabular-nums">{s.horizon_days}d</td>
                    <td className="p-2 text-muted-foreground">{s.author}</td>
                    <td className="p-2 tabular-nums text-muted-foreground">{fmtDate(s.created_at)}</td>
                    <td className="p-2 tabular-nums">{days.filter((d) => d.changed).length}</td>
                    <td className="p-2">
                      <span className="flex items-center gap-1.5 justify-end">
                        <button data-testid={`scenario-apply-${s.scenario_id}`} onClick={() => openApply(s)} disabled={applying === s.scenario_id}
                          className="text-xs border border-foreground px-2 py-1 hover:bg-foreground hover:text-background transition-colors disabled:opacity-40">
                          {applying === s.scenario_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
                        </button>
                        <button data-testid={`scenario-template-${s.scenario_id}`} title="Save as reusable template"
                          onClick={async () => {
                            const name = window.prompt("Name this template", s.name);
                            if (!name) return;
                            await api.post(`/scenarios/${s.scenario_id}/save-as-template`, { name });
                            toast.success("Saved as a template · apply it to any squad in two clicks");
                            load();
                          }}
                          className="text-muted-foreground hover:text-foreground"><BookmarkPlus className="w-3.5 h-3.5" /></button>
                        <button data-testid={`scenario-dup-${s.scenario_id}`} title="Duplicate" onClick={async () => { await api.post(`/scenarios/${s.scenario_id}/duplicate`); load(); }}
                          className="text-muted-foreground hover:text-foreground"><Copy className="w-3.5 h-3.5" /></button>
                        <button data-testid={`scenario-del-${s.scenario_id}`} title="Delete" onClick={async () => { await api.delete(`/scenarios/${s.scenario_id}`); load(); }}
                          className="text-muted-foreground hover:text-traffic-red"><Trash2 className="w-3.5 h-3.5" /></button>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="border-t border-border" data-testid="scenario-templates">
        <div className="px-4 py-3 flex flex-wrap items-center gap-2">
          <Layers className="w-4 h-4" />
          <span className="text-xs tracking-[0.18em] uppercase font-bold">Templates</span>
          <span className="text-xs text-muted-foreground">reusable shapes you can drop on any squad</span>
        </div>
        {templates.length === 0 ? (
          <p className="px-4 pb-4 text-xs text-muted-foreground" data-testid="templates-empty">
            No templates yet. Save a scenario you like as a template and it becomes two clicks on any squad.
          </p>
        ) : (
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            {templates.map((t) => (
              <div key={t.scenario_id} className="border border-border p-2.5 min-w-[190px]" data-testid={`template-${t.scenario_id}`}>
                <div className="font-medium text-sm truncate">{t.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  {t.shape?.recovery_days || 0} recovery · {t.shape?.taper_days || 0}d taper · {t.horizon_days}d
                </div>
                <div className="flex gap-1.5 mt-2">
                  <button data-testid={`template-apply-${t.scenario_id}`} onClick={() => openApply(t)}
                    className="text-xs border border-foreground px-2 py-1 hover:bg-foreground hover:text-background transition-colors">Apply to squad</button>
                  <button data-testid={`template-del-${t.scenario_id}`} title="Delete template"
                    onClick={async () => { await api.delete(`/scenario-templates/${t.scenario_id}`); load(); }}
                    className="text-muted-foreground hover:text-traffic-red"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-border flex flex-wrap gap-2 items-center">
        <button data-testid="scenario-compare" onClick={compare} className="text-sm px-3 py-2 border border-border hover:bg-surface transition-colors flex items-center gap-1.5">
          <GitCompare className="w-3.5 h-3.5" /> Compare selected ({picked.length})
        </button>
        {cmp && <button onClick={() => setCmp(null)} className="text-xs text-muted-foreground hover:text-foreground">clear</button>}
      </div>

      {cmp && (
        <div className="border-t border-border scroll-x" data-testid="scenario-compare-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left p-2">Scenario</th><th className="p-2">Changed days</th><th className="p-2">Sessions</th>
                <th className="p-2">Readiness</th><th className="p-2">Rest+</th><th className="p-2">Sharpness</th><th className="p-2">Injury risk</th>
              </tr>
            </thead>
            <tbody>
              {cmp.map((s) => (
                <tr key={s.scenario_id} className="border-b border-border last:border-0">
                  <td className="p-2 font-medium">{s.name}</td>
                  <td className="p-2 text-center tabular-nums">{s.changed_days}</td>
                  <td className="p-2 text-center tabular-nums">{s.session_count}</td>
                  <td className="p-2 text-center tabular-nums font-bold">{s.final?.readiness ?? "-"}</td>
                  <td className="p-2 text-center tabular-nums">{s.final?.rest ?? "-"}</td>
                  <td className="p-2 text-center tabular-nums">{s.final?.sharpness ?? "-"}</td>
                  <td className="p-2 text-center tabular-nums">{s.final?.injury_risk ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start sm:items-center justify-center p-2 sm:p-6 overflow-y-auto" onClick={() => setPreview(null)}>
          <div className="bg-background border border-border w-full max-w-lg max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="scenario-apply-modal">
            <div className="px-4 sm:px-6 h-14 border-b border-border flex items-center font-heading font-bold tracking-tight shrink-0">
              Apply “{preview.scenario.name}”
            </div>
            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto text-sm flex-1">
              <div className="border border-border p-3 space-y-3" data-testid="apply-target">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Apply to</div>
                <div className="flex flex-wrap gap-2">
                  {[["athlete", "This athlete"], ["squad", "A whole squad"], ["picked", "Pick athletes"]].map(([v, l]) => (
                    <button key={v} data-testid={`apply-target-${v}`} onClick={() => setApplyTo(v)}
                      className={`text-xs px-3 py-1.5 border transition-colors ${applyTo === v ? "border-foreground bg-foreground text-background" : "border-border hover:bg-surface"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {applyTo === "squad" && (
                  <select data-testid="apply-squad-select" value={squad} onChange={(e) => setSquad(e.target.value)} className="inp text-sm">
                    <option value="">Choose squad…</option>
                    {squads.map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                )}
                {applyTo === "picked" && (
                  <div className="max-h-40 overflow-y-auto grid sm:grid-cols-2 gap-px bg-border border border-border">
                    {roster.map((a) => (
                      <label key={a.athlete_id} data-testid={`apply-pick-${a.athlete_id}`}
                        className="bg-background p-2 flex items-center gap-2 text-xs cursor-pointer hover:bg-surface">
                        <input type="checkbox" checked={pickedAthletes.includes(a.athlete_id)}
                          onChange={(e) => setPickedAthletes((p) => e.target.checked ? [...p, a.athlete_id] : p.filter((x) => x !== a.athlete_id))} />
                        <span className="flex-1 truncate">{a.name}</span>
                        <span className="text-muted-foreground">{a.squad}</span>
                      </label>
                    ))}
                  </div>
                )}
                <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="apply-scale-toggle">
                  <input type="checkbox" checked={scale} onChange={(e) => setScale(e.target.checked)} className="mt-0.5" />
                  <span>
                    Scale load to each athlete's own baseline
                    <span className="block text-muted-foreground">
                      Session durations are multiplied by that athlete's 30-day chronic load relative to the athlete this
                      scenario was built on, capped at ±40%. Turn this off to apply the prescription exactly as written.
                    </span>
                  </span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="apply-skip-unavailable">
                  <input type="checkbox" checked={skipUnavailable} onChange={(e) => setSkipUnavailable(e.target.checked)} />
                  Skip athletes a physio has marked unavailable
                </label>
                <button data-testid="apply-refresh-preview" onClick={refreshPreview}
                  className="text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors">Refresh preview</button>
              </div>

              {preview.note && <p className="text-[11px] text-muted-foreground" data-testid="apply-note">{preview.note}</p>}
              {preview.athletes.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="apply-empty">No athletes selected yet.</p>
              )}
              {preview.athletes.map((a) => (
                <div key={a.athlete_id} className={`border border-border p-3 ${a.excluded ? "opacity-60" : ""}`} data-testid={`apply-athlete-${a.athlete_id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{a.name}</span>
                    {a.squad && <span className="text-xs text-muted-foreground">{a.squad}</span>}
                    {a.excluded && <span className="text-[10px] uppercase tracking-widest border border-traffic-amber text-traffic-amber px-1.5 py-0.5" data-testid={`apply-excluded-${a.athlete_id}`}>skipped · {a.excluded_reason}</span>}
                    {!a.can_apply && <span className="ml-auto text-[10px] uppercase tracking-widest border border-traffic-amber text-traffic-amber px-1.5 py-0.5">read-only for your role</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground" data-testid={`apply-counts-${a.athlete_id}`}>
                    <span>{a.changed_days} day(s)</span>
                    <span>{a.sessions_removed} session(s) → recovery</span>
                    <span>{a.sessions_reduced} tapered</span>
                    <span>load ×<b className="text-foreground tabular-nums">{a.scale_factor}</b></span>
                  </div>
                  {(a.warnings || []).map((w, i) => (
                    <label key={i} className="flex gap-2 items-start mt-2 text-xs cursor-pointer" data-testid={`warning-${w.code}`}>
                      <input type="checkbox" checked={ack.includes(w.code)} className="mt-0.5"
                        onChange={(e) => setAck((p) => e.target.checked ? [...p, w.code] : p.filter((x) => x !== w.code))} />
                      <span className={w.severity === "high" ? "text-traffic-red" : w.severity === "medium" ? "text-traffic-amber" : "text-muted-foreground"}>
                        <AlertTriangle className="w-3 h-3 inline mr-1" />{w.text}
                        <span className="block text-muted-foreground">Acknowledging is logged against this change and notifies the owning role. It does not block you.</span>
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <div className="px-4 sm:px-6 py-3 border-t border-border flex flex-wrap gap-2 justify-end shrink-0">
              <button onClick={() => setPreview(null)} className="text-sm px-4 py-2 border border-border hover:bg-surface transition-colors">Cancel</button>
              {canApply ? (
                <button data-testid="scenario-apply-confirm" onClick={confirmApply} disabled={!ackDone || applying}
                  className="text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors disabled:opacity-40 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> {ackDone ? `Apply to ${targets.length} athlete(s)` : "Acknowledge warnings first"}
                </button>
              ) : (
                <button data-testid="scenario-request" onClick={() => request(preview.scenario, "load")}
                  className="text-sm px-4 py-2 border border-foreground hover:bg-surface transition-colors flex items-center gap-1.5">
                  <Send className="w-4 h-4" /> Request from Head Coach
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
